import { type RecordId } from '../types';

/*
 * This is a set of utility methods for sorting a sparse collection (i.e., where
 * the items are "far apart"). The idea with the sparse collection is to be able
 * to insert and move objects within a collection while minimizing the frequency
 * entire list (re)sorts.
 */

const MIN_INDEX = Number.MIN_SAFE_INTEGER;
const MAX_INDEX = Number.MAX_SAFE_INTEGER;
// Default offset between items in order to minimize rebalancing.
const INDEX_OFFSET = 1000000;
// Don't update an Z value during rebalancing if it moves by less than this
// amount, and doesn't strictly need to move.
const SLOP = INDEX_OFFSET / 10;

type SortFunc = (a: any, b: any) => void;

// Simple interface to ensure the key is a valid key on the object. Also allows
// for an option set() method to be used instead of directly setting the key.
type Sortable<T extends string> = Record<T, any> & {
  id: RecordId;
  set?: (key: T, value: any) => void;
};

function allocateAtTop<T extends string>(
  collection: Sortable<T>[],
  key: T,
  sort: SortFunc = sortCollection
) {
  if (collection.length > 0) {
    const getTop = () => collection.findLast(obj => obj[key])?.[key] ?? 0;
    let currentTop = getTop();
    if (currentTop > MAX_INDEX - INDEX_OFFSET) {
      rebalance(collection, key, sort);
      currentTop = getTop();
    }
    return currentTop + INDEX_OFFSET;
  } else {
    return 0;
  }
}

function set<T extends string>(object: Sortable<T>, key: T, value: any) {
  if (typeof object.set === 'function') {
    object.set(key, value);
  } else {
    object[key] = value;
  }
}

// Make space to fit more indexes based on 'key'. We want the rebalancing to be
// stable so that if two clients both rebalance at the same time they end up
// using the same indexes - even if they have different numbers of elements (e.g.
// one client might be inserting one record and one might be inserting two).
// We do this by leaving alone items that don't need to change much.
function rebalance<T extends string>(
  collection: Sortable<T>[],
  key: T,
  sort: SortFunc = sortCollection
) {
  let currentIndex = 0;
  let minIndex = -INDEX_OFFSET;

  // Make sure the collection is sorted - shouldn't be necessary, but just in
  // case because the following algorithm will go horribly wrong if it is
  // not sorted.
  sort(collection, key);

  collection.forEach(obj => {
    if (!obj[key]) {
      set(obj, key, currentIndex);
    } else {
      // See if the current object index is close enough that we don't need to
      // modify it.
      if (
        obj[key] > minIndex &&
        obj[key] > currentIndex - SLOP &&
        obj[key] < currentIndex + SLOP
      ) {
        // Leave it alone.
      } else {
        set(obj, key, currentIndex);
      }
    }
    minIndex = obj[key];
    currentIndex = currentIndex + INDEX_OFFSET;
  });
}

function sortCollection<T extends string>(collection: Sortable<T>[], key) {
  collection.sort((a, b) => {
    return a[key] - b[key];
  });
}

function insert<T extends string>(
  collection: Sortable<T>[],
  object: Sortable<T>,
  key: T,
  sort: SortFunc = sortCollection
): number {
  let objectOfKey = object[key];

  // Specifically == to check for undefined or null
  if (objectOfKey == null) {
    // @ts-expect-error - objectOfKey's value is of type any
    objectOfKey = allocateAtTop(collection, key, sort);
  }

  const prevIndex = collection.findLastIndex(
    prevObj => prevObj[key] <= objectOfKey
  );
  if (prevIndex === -1) {
    collection.splice(0, 0, object);
  } else {
    // Check for collision.
    const prevKey = collection[prevIndex][key];
    if (prevKey === objectOfKey) {
      const nextIndex = prevIndex + 1;
      if (
        collection[nextIndex] &&
        collection[nextIndex][key] - prevKey < INDEX_OFFSET * 2
      ) {
        // @ts-expect-error - objectOfKey's value is of type any
        objectOfKey = Math.floor((collection[nextIndex][key] + prevKey) / 2);
        if (objectOfKey === prevKey) {
          rebalance(collection, key, sort);
        }
      } else {
        // @ts-expect-error - objectOfKey's value is of type any
        objectOfKey = prevKey + INDEX_OFFSET;
      }
    }
    collection.splice(prevIndex + 1, 0, object);
  }

  return objectOfKey;
}

function moveBefore<T extends string>(
  collection: Sortable<T>[],
  object: Sortable<T>,
  beforeIndex: number,
  key: T,
  sort: SortFunc = sortCollection
) {
  if (beforeIndex >= collection.length) {
    set(object, key, 0);
  } else if (collection.length === 0) {
    set(object, key, 0);
  } else {
    const beforeValue = collection[beforeIndex][key] || 0;
    if (beforeIndex === 0) {
      if (beforeValue < MIN_INDEX + INDEX_OFFSET) {
        rebalance(collection, key, sort);
      }
      set(object, key, beforeValue - INDEX_OFFSET);
    } else {
      const previousValue = collection[beforeIndex - 1][key] || 0;
      if (beforeValue - previousValue < 2) {
        rebalance(collection, key, sort);
      }
      set(object, key, Math.floor((beforeValue + previousValue) / 2));
    }
  }

  const fromIndex = collection.findIndex(obj => obj.id === object.id);
  if (fromIndex === -1)
    throw new Error(`Can't find object in objects ${object.id}`);
  collection.splice(fromIndex, 1);
  collection.splice(beforeIndex, 0, object);

  sort(collection, key);
}

function moveAfter<T extends string>(
  collection: Sortable<T>[],
  object: Sortable<T>,
  afterIndex: number,
  key: T,
  sort: SortFunc = sortCollection
) {
  setValueForAfterIndex(collection, object, afterIndex, key);
  const fromIndex = collection.findIndex(obj => obj.id === object.id);
  if (fromIndex === -1)
    throw new Error(`Can't find object in objects ${object.id}`);
  collection.splice(fromIndex, 1);
  collection.splice(afterIndex, 0, object);
  sort(collection, key);
}

// Used in moveAfter to set the value in object[key]
function setValueForAfterIndex<T extends string>(
  collection: Sortable<T>[],
  object: Sortable<T>,
  afterIndex: number,
  key: T,
  sort: SortFunc = sortCollection
) {
  if (afterIndex > collection.length) {
    set(object, key, 0);
  } else {
    const afterKey = collection[afterIndex][key] || 0;
    if (afterIndex === collection.length - 1) {
      if (afterKey > MAX_INDEX - INDEX_OFFSET) {
        rebalance(collection, key, sort);
      }
      set(object, key, afterKey + INDEX_OFFSET);
    } else {
      const nextKey = collection[afterIndex + 1][key] || 0;
      if (nextKey - afterKey < 2) {
        rebalance(collection, key, sort);
      }
      set(object, key, Math.floor((afterKey + nextKey) / 2));
    }
  }
}

export {
  allocateAtTop,
  insert,
  moveBefore,
  moveAfter,
  setValueForAfterIndex,
  rebalance,
  sortCollection,
};
